"use client";

import { useEffect, useState } from "react";

// Star/X shape
const STAR_PATH =
  "M5.98055 10.31466C4.21642 7.29044 3.33436 5.77833 3.52636 4.99339C3.7036 4.26883 4.26932 3.70311 4.99388 3.52588C5.77882 3.33387 7.29093 4.21593 10.3152 5.98006C10.8872 6.31375 11.1732 6.48059 11.477 6.54831C11.7636 6.61218 12.0607 6.61218 12.3472 6.54831C12.651 6.48059 12.937 6.31375 13.5091 5.98006C16.5333 4.21593 18.0454 3.33387 18.8303 3.52588C19.5549 3.70311 20.1206 4.26883 20.2979 4.99339C20.4899 5.77833 19.6078 7.29044 17.8437 10.31466C17.51 10.8867 17.3431 11.1727 17.2754 11.4765C17.2115 11.7631 17.2115 12.0602 17.2754 12.3467C17.3431 12.6505 17.51 12.9365 17.8437 13.5086C19.6078 16.5328 20.4899 18.0449 20.2979 18.8299C20.1206 19.5544 19.5549 20.1201 18.8303 20.2974C18.0454 20.4894 16.5333 19.6073 13.5091 17.8432C12.937 17.5095 12.651 17.3426 12.3472 17.2749C12.0607 17.2111 11.7636 17.2111 11.477 17.2749C11.1732 17.3426 10.8872 17.5095 10.3152 17.8432C7.29093 19.6073 5.77882 20.4894 4.99388 20.2974C4.26932 20.1201 3.7036 19.5544 3.52636 18.8299C3.33436 18.0449 4.21642 16.5328 5.98055 13.5086C6.31424 12.9365 6.48108 12.6505 6.5488 12.3467C6.61267 12.0602 6.61267 11.7631 6.5488 11.4765C6.48108 11.1727 6.31424 10.8867 5.98055 10.31466Z";

// Rounded square shape
const SQUARE_PATH =
  "M0 12C0 8.27247 0 6.4087 0.608964 4.93853C1.42092 2.97831 2.97831 1.42092 4.93853 0.608964C6.4087 0 8.27247 0 12 0C15.7275 0 17.5913 0 19.0615 0.608964C21.0217 1.42092 22.5791 2.97831 23.391 4.93853C24 6.4087 24 8.27247 24 12C24 15.7275 24 17.5913 23.391 19.0615C22.5791 21.0217 21.0217 22.5791 19.0615 23.391C17.5913 24 15.7275 24 12 24C8.27247 24 6.4087 24 4.93853 23.391C2.97831 22.5791 1.42092 21.0217 0.608964 19.0615C0 17.5913 0 15.7275 0 12Z";

// Oval shape
const OVAL_PATH =
  "M12 24C7.02944 24 3 19.9706 3 15L3 12L3 9C3 4.02944 7.02944 0 12 0C16.9706 0 21 4.02944 21 9L21 12L21 15C21 19.9706 16.9706 24 12 24Z";

const SHAPES = [STAR_PATH, OVAL_PATH, SQUARE_PATH];

const COLORS = [
  "#2DA5A2", // teal
  "#6A4A76", // purple
  "#B2C995", // green
  "#4C6EBE", // blue
  "#B16786", // mauve
  "#9ABDB1", // mint
];

const CYCLE_DURATION = 700; // Total cycle duration (hold + morph)
const MORPH_DURATION = 200; // Morph transition duration
const ROTATION_ANGLE = -45; // Anti-clockwise rotation during transition

export function ThinkingIndicator({
  children,
  active = true,
  transitionDuration = 500,
  cycleDuration = CYCLE_DURATION,
}: {
  children?: React.ReactNode;
  active?: boolean;
  transitionDuration?: number;
  cycleDuration?: number;
}) {
  const [shapeIndex, setShapeIndex] = useState(0);
  const [prevShapeIndex, setPrevShapeIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);
  const [rotation, setRotation] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!active) return;

    let timeoutRef: number | null;

    const intervalRef = window.setInterval(() => {
      setShapeIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % SHAPES.length;
        setPrevShapeIndex(prevIndex); // Store current as previous
        setIsTransitioning(true);

        // End transition and sync prevIndex after morph completes
        if (timeoutRef) clearTimeout(timeoutRef);
        timeoutRef = null;
        timeoutRef = window.setTimeout(() => {
          setIsTransitioning(false);
          setPrevShapeIndex(nextIndex);
        }, MORPH_DURATION);

        return nextIndex;
      });

      setColorIndex((prev) => (prev + 1) % COLORS.length);
      setRotation((prev) => prev + ROTATION_ANGLE);
    }, cycleDuration);

    return () => {
      clearInterval(intervalRef);
      if (timeoutRef) clearTimeout(timeoutRef);
    };
  }, [active, transitionDuration, cycleDuration]);

  const currentColor = COLORS[colorIndex];

  return (
    <>
      <style>
        {`
          @keyframes bounce-scale {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.1); }
          }
        `}
      </style>
      <div
        aria-label="Assistant is thinking"
        role="status"
        className={`inline-flex items-center gap-2 pt-4 pl-1 ${active ? "opacity-100" : "opacity-0 transition-opacity"}`}
        style={active ? undefined : { transitionDuration: `${transitionDuration}ms` }}
      >
        <div className="relative flex items-center justify-center size-6 overflow-visible">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full overflow-visible"
          >
            {/* Outer g: rotation */}
            <g
              transform={`rotate(${rotation} 12 12)`}
              style={{
                transition: `transform ${MORPH_DURATION}ms ease-in-out`,
              }}
            >
              {/* Inner g: scale bounce */}
              <g
                style={{
                  transformOrigin: "12px 12px",
                  animation: isTransitioning
                    ? `bounce-scale ${MORPH_DURATION}ms ease-out`
                    : undefined,
                }}
              >
                {prevShapeIndex !== shapeIndex ? (
                  <>
                    {/* Previous shape - fades out during transition */}
                    <path
                      d={SHAPES[prevShapeIndex]}
                      fill={currentColor}
                      style={{
                        opacity: isTransitioning ? 0 : 1,
                        transition: `opacity ${MORPH_DURATION}ms ease-in-out, fill ${MORPH_DURATION}ms ease-in-out`,
                      }}
                    />
                    {/* Current shape - fades in during transition */}
                    <path
                      d={SHAPES[shapeIndex]}
                      fill={currentColor}
                      style={{
                        opacity: isTransitioning ? 1 : 0,
                        transition: `opacity ${MORPH_DURATION}ms ease-in-out, fill ${MORPH_DURATION}ms ease-in-out`,
                      }}
                    />
                  </>
                ) : (
                  /* When idle, show only current shape */
                  <path d={SHAPES[shapeIndex]} fill={currentColor} />
                )}
              </g>
            </g>
          </svg>
        </div>
        <span
          className="text-sm font-medium transition-colors duration-300"
          style={{ color: currentColor }}
        >
          {children ?? "Thinking..."}
        </span>
      </div>
    </>
  );
}
